import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Newspaper, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CATS = [
  ["headline", "Berita Headline"],
  ["showtynion_trip", "Showtynion Trip"],
  ["beauty_on_you", "Beauty on You (Vol 1-6)"],
  ["jastip_elite", "Jastip Elite"],
  ["clbk", "CLBK — Curhat Lagi Bersama Kita"],
  ["blind_date", "Blind Date"],
  ["fwb", "FWB — Friends With Beauty"],
  ["mualab", "MUALAB"],
];
const catLabel = (id) => (CATS.find(([v]) => v === id)?.[1]) || id;

export default function NewsPanel() {
  const [news, setNews] = useState([]);
  const load = useCallback(() => api.get("/news").then((r) => setNews(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/news/${id}`);
      load();
    } catch (e) {
      toast.error("Gagal menghapus berita");
    }
  };

  const approve = async (id) => {
    try { await api.post(`/news/${id}/status`, { status: "approved" }); load(); toast.success("Berita di-approve & tayang ke member"); }
    catch (e) { toast.error("Gagal approve"); }
  };

  return (
    <div data-testid="news-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-2xl text-showty-navy">News & Events Blog</h3>
          <p className="text-sm text-muted-foreground">Kelola berita event yang lalu (Past) & mendatang (New).</p>
        </div>
        <AddNewsDialog onDone={load} />
      </div>

      {news.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/50 py-14 text-center text-sm text-muted-foreground" data-testid="news-empty">
          <Newspaper className="w-6 h-6 mx-auto mb-2 text-showty-red/50" /> Belum ada berita
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {news.map((n) => (
            <div key={n.id} className="rounded-2xl border border-border bg-white overflow-hidden flex flex-col" data-testid={`news-${n.id}`}>
              <div className="h-40 bg-showty-pink/30 overflow-hidden">
                {n.cover_image && <img src={n.cover_image} alt={n.title} className="w-full h-full object-cover" />}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-wide font-semibold text-showty-red">{catLabel(n.category)}</span>
                  <div className="flex items-center gap-2">
                    {n.status === "approved"
                      ? <span className="text-[10px] rounded-full bg-green-100 text-green-700 px-2 py-0.5 flex items-center gap-1" data-testid={`news-status-${n.id}`}><CheckCircle2 className="w-3 h-3" /> Approved</span>
                      : <button onClick={() => approve(n.id)} className="text-[10px] rounded-full bg-showty-gold/20 text-showty-navy px-2 py-0.5 flex items-center gap-1 hover:bg-showty-gold/40" data-testid={`news-approve-${n.id}`}><Clock className="w-3 h-3" /> Review · Approve</button>}
                    <button onClick={() => remove(n.id)} className="text-muted-foreground hover:text-destructive" data-testid={`news-delete-${n.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <h4 className="mt-2 font-display text-xl text-showty-navy">{n.title}</h4>
                {n.date && <p className="text-xs text-muted-foreground mt-0.5">{n.date}</p>}
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3 flex-1">{n.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const AddNewsDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const empty = { title: "", category: "headline", cover_image: "", website_url: "", body: "", date: "" };
  const [f, setF] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/media/upload", fd); set("cover_image", r.data.url); toast.success("Gambar terupload"); }
    catch { toast.error("Gagal upload gambar"); }
    setUploading(false); e.target.value = "";
  };
  const save = async () => {
    if (!f.title) return toast.error("Judul wajib diisi");
    await api.post("/news", f);
    toast.success("Berita ditambahkan");
    setOpen(false); setF(empty); onDone?.();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="news-add-btn"><Plus className="w-4 h-4 mr-1" /> Tulis Berita</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="news-add-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">Tulis Berita / Blog Event</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Judul</Label><Input className="mt-1" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="news-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kategori</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1" data-testid="news-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tanggal</Label><Input className="mt-1" placeholder="2026-07-18" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="news-date" /></div>
          </div>
          <div>
            <Label>Upload Gambar / Video</Label>
            <Input type="file" accept="image/*,video/*" className="mt-1" onChange={upload} data-testid="news-upload" />
            {uploading && <p className="text-xs text-showty-red mt-1">Mengupload…</p>}
            {f.cover_image && <img src={f.cover_image} alt="preview" className="mt-2 h-28 w-full object-cover rounded-lg border border-border" data-testid="news-cover-preview" />}
          </div>
          <div><Label>Atau URL Gambar (opsional)</Label><Input className="mt-1" placeholder="https://..." value={f.cover_image} onChange={(e) => set("cover_image", e.target.value)} data-testid="news-cover" /></div>
          <div><Label>URL Website (opsional)</Label><Input className="mt-1" placeholder="https://showtynion.com/event" value={f.website_url} onChange={(e) => set("website_url", e.target.value)} data-testid="news-website" /></div>
          <div><Label>Isi Berita</Label><Textarea rows={5} className="mt-1" value={f.body} onChange={(e) => set("body", e.target.value)} data-testid="news-body" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={uploading} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="news-add-save">Publikasikan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
